import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../core/services/core.translate.service';
import { UserService } from '../../core/services/core.user.service';
import { TourService } from '../../core/modules/tour/tour.service';
import { ProjectModel } from '../../core/services/project.model.service';
import { WikiService } from '../../widgets/wiki/services/wiki.service';
import { WikiHelperService } from '../../widgets/wiki/services/wiki.helper';
import { AccessDeniedService } from '../../core/services/core.access-denied.service';

@Injectable({ providedIn: 'root' })
export class WikiResolvesService {

    constructor(
        private stateService: StateService,
        private translateService: GfTranslateService,
        private userService: UserService,
        private tourService: TourService,
        private projectModel: ProjectModel,
        private wikiService: WikiService,
        private wikiHelper: WikiHelperService,
        private accessDeniedService: AccessDeniedService
    ) {
    }

    // Security check - can access wiki (read or higher)
    security(project: any) {
        return this.projectModel.checkPermission(project, 'wiki');
    }

    // Security check - can write wiki (edit or admin only)
    canWrite(project: any) {
        const level = _.get(project, 'rel.permissions.wiki', 0);
        return this.projectModel.assertPermission(level > 1);
    }

    // Get lock status for wiki page
    async getLockStatus(unixName: string, pagename: string = 'FrontPage'): Promise<any> {
        const params = {
            project: unixName
        };

        try {
            const response = await this.wikiService.getLock(pagename, params);
            return response;
        } catch (response) {
            return { flags: 0 };
        }
    }

    // Get appropriate tour based on user permissions
    getWikiTour(project: any) {
        const canEdit = this.userService.session.hasUser() &&
            _.get(project, 'rel.permissions.wiki', 0) > 1;

        if (canEdit) {
            return this.tourService.setTour('wikiPageEditCtrl');
        } else {
            return this.tourService.setTour('wikiPageViewCtrl');
        }
    }

    // Get wiki page with error handling and fallback
    async getWikiPage(unixName: string, pagename: string = 'FrontPage'): Promise<any> {
        return this.getWikiPageScoped({ project: unixName }, pagename);
    }

    /**
     * Fetch a wiki page for an arbitrary scope. Pass `{ project: unixName }` for a project wiki,
     * or `{ section, refId }` for a section-scoped wiki (e.g. the admin/site homepage). Used by
     * `<wiki-container>` so it can load its own page regardless of where it is embedded.
     */
    async getWikiPageScoped(scope: any, pagename: string = 'FrontPage'): Promise<any> {
        const id = _.trim(pagename) || 'FrontPage';

        try {
            const data: any = await this.wikiService.info(encodeURIComponent(id), {
                rel: 'monitor,options,version,content',
                ...scope
            });
            if (data && data.pagename) {
                data.pagename = decodeURIComponent(data.pagename);
            }
            return data;
        } catch (error) {
            // Create fallback wiki page when not found
            const wikiPage: any = {
                pagename: id,
                rel: {
                    content: {
                        data: this.wikiHelper.getContents({ pagename: id })
                    }
                }
            };
            return wikiPage;
        }
    }

    async getAttachmentCount(wikiPage: any, project: any): Promise<number> {
        if (!wikiPage || !wikiPage.id) { return 0; }
        if (_.get(project, 'rel.permissions.wiki', 0) <= 1) { return 0; }
        try {
            const response: any = await this.wikiService.getAttachments(wikiPage.id);
            return (response && response.items) ? response.items.length : 0;
        } catch {
            return 0;
        }
    }

    async getVersionCount(wikiPage: any, project: any): Promise<number> {
        if (!wikiPage || !wikiPage.id) { return 0; }
        if (_.get(project, 'rel.permissions.wiki', 0) <= 1) { return 0; }
        try {
            const response: any = await this.wikiService.versions(wikiPage.id, {});
            return (response && response.items) ? response.items.length : 0;
        } catch {
            return 0;
        }
    }

    // Get monitors for specific wiki page
    async getPageMonitors(wikiPage: any) {
        // Return empty list if wiki page doesn't have an ID (new page not yet saved)
        if (!wikiPage || !wikiPage.id) {
            return { items: [] };
        }
        try {
            return await this.wikiService.getMonitorList('wikipage', wikiPage.id);
        } catch (error) {
            return { items: [] };
        }
    }

    // Get monitors for entire wiki
    async getWikiMonitors(project: any) {
        if (!project || !project.id) {
            return { items: [] };
        }
        try {
            return await this.wikiService.getMonitorList('wiki', project.id);
        } catch (error) {
            return { items: [] };
        }
    }

    // Get project ID helper
    getProjectId(project: any) {
        return project.id;
    }

    // Get page name helper
    getPagename(wikiPage: any) {
        return wikiPage.pagename;
    }

    // Tour setup for wiki browse
    getBrowseTour() {
        return this.tourService.setTour('wikiBrowseCtrl');
    }

    // TODO: this method is not being used, except in unit tests. Analyze if we need it and decide removal.
    // Get wiki page with validation and error handling (for routing)
    async getWikiPageWithValidation(unixName: string, pagename: string = 'FrontPage', transition?: any): Promise<any> {
        try {
            const wikiPage = await this.getWikiPage(unixName, pagename);
            return wikiPage;
        } catch (error) {
            this.accessDeniedService.goRefused(transition, {
                message: this.translateService.translate(
                    "Sorry, that Wiki page does not exist, or you are not allowed to view it."
                )
            });
            throw error;
        }
    }

    // TODO: this method is not being used, except in unit tests. Analyze if we need it and decide removal.
    // Home wiki route - fetches page and redirects if needed
    async getHomepageWiki(projectUnixName: string, pagename: string = 'FrontPage', transition?: any): Promise<any> {
        try {
            const wikiPage = await this.getWikiPage(projectUnixName, pagename);
            
            // Navigate to the wiki page view
            const params = { unixName: projectUnixName, pagename: pagename };
            await this.stateService.go('home.project.summary.wiki.page', params);
            return wikiPage;
        } catch (error) {
            this.accessDeniedService.goRefused(transition, {
                message: this.translateService.translate(
                    "Sorry, that Wiki page does not exist, or you are not allowed to view it."
                )
            });
            throw error;
        }
    }
}
